import React, { useState, useRef, useEffect, cloneElement, isValidElement } from 'react';

const OverlayTrigger = ({ 
  children, 
  overlay, 
  placement = 'top',
  trigger = 'hover', // 'hover', 'click', 'focus', or array like ['hover', 'focus']
  delay = 0,
  show: controlledShow,
  onToggle,
  ...props 
}) => {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const timeoutRef = useRef(null);
  
  // Use controlled or uncontrolled state
  const isControlled = controlledShow !== undefined;
  const isVisible = isControlled ? controlledShow : show;

  const triggers = Array.isArray(trigger) ? trigger : [trigger];
  const showDelay =
    typeof delay === 'object' && delay !== null ? Number(delay.show) || 0 : Number(delay) || 0;

  const resolveOverlay = () => {
    if (typeof overlay === 'function') {
      return overlay({ placement });
    }

    if (isValidElement(overlay)) {
      return cloneElement(overlay, { placement });
    }

    return null;
  };

  const handleShow = () => {
    if (showDelay > 0) {
      timeoutRef.current = setTimeout(() => {
        if (isControlled && onToggle) {
          onToggle(true);
        } else {
          setShow(true);
        }
      }, showDelay);
    } else {
      if (isControlled && onToggle) {
        onToggle(true);
      } else {
        setShow(true);
      }
    }
  };

  const handleHide = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (isControlled && onToggle) {
      onToggle(false);
    } else {
      setShow(false);
    }
  };

  const handleToggle = () => {
    if (isVisible) {
      handleHide();
    } else {
      handleShow();
    }
  };

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      let top = 0;
      let left = 0;

      switch (placement) {
        case 'top':
          top = rect.top + scrollTop;
          left = rect.left + scrollLeft + rect.width / 2;
          break;
        case 'bottom':
          top = rect.bottom + scrollTop;
          left = rect.left + scrollLeft + rect.width / 2;
          break;
        case 'left':
          top = rect.top + scrollTop + rect.height / 2;
          left = rect.left + scrollLeft;
          break;
        case 'right':
          top = rect.top + scrollTop + rect.height / 2;
          left = rect.right + scrollLeft;
          break;
        default:
          break;
      }

      setPosition({ top, left });
    }
  }, [isVisible, placement]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Click outside handler
  useEffect(() => {
    if (triggers.includes('click') && isVisible) {
      const handleClickOutside = (event) => {
        if (triggerRef.current && !triggerRef.current.contains(event.target)) {
          handleHide();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isVisible, triggers]);

  const childProps = {};

  if (triggers.includes('hover')) {
    childProps.onMouseEnter = handleShow;
    childProps.onMouseLeave = handleHide;
  }

  if (triggers.includes('click')) {
    childProps.onClick = handleToggle;
  }

  if (triggers.includes('focus')) {
    childProps.onFocus = handleShow;
    childProps.onBlur = handleHide;
  }

  return (
    <>
      <div ref={triggerRef} className="inline-block" {...childProps}>
        {children}
      </div>
      
      {isVisible && (
        <div 
          style={{
            position: 'absolute',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 9999,
          }}
        >
          {resolveOverlay()}
        </div>
      )}
    </>
  );
};

export default OverlayTrigger;
